/**
 * Map Settings Panel
 *
 * Floating settings panel for quiz map display options.
 * Allows users to customize map rendering (borders, hover, feedback, labels).
 * Settings are locked after the first question to ensure fair difficulty tracking.
 */

import { Settings, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Separator } from "@/components/ui/separator";
import { useQuizStore, selectMapSettings, selectSettingsLocked, updateMapSettings } from "@/stores/useQuizStore";
import { useState } from "react";

interface MapSettingsPanelProps {
  hideOnMobileWhenLocked?: boolean;
}

export function MapSettingsPanel({ hideOnMobileWhenLocked = false }: MapSettingsPanelProps) {
  const mapSettings = useQuizStore(selectMapSettings);
  const settingsLocked = useQuizStore(selectSettingsLocked);
  const [isOpen, setIsOpen] = useState(false);

  const handleToggle = (key: keyof typeof mapSettings, value: boolean) => {
    if (settingsLocked) return;
    updateMapSettings({ [key]: value });
  };

  // Hide on mobile when settings are locked (if prop is true)
  if (hideOnMobileWhenLocked && settingsLocked) {
    return <div className="sm:block hidden"><InnerPanel isOpen={isOpen} setIsOpen={setIsOpen} mapSettings={mapSettings} settingsLocked={settingsLocked} handleToggle={handleToggle} /></div>;
  }

  return <InnerPanel isOpen={isOpen} setIsOpen={setIsOpen} mapSettings={mapSettings} settingsLocked={settingsLocked} handleToggle={handleToggle} />;
}

function InnerPanel({ isOpen, setIsOpen, mapSettings, settingsLocked, handleToggle }: {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  mapSettings: any;
  settingsLocked: boolean;
  handleToggle: (key: string, value: boolean) => void;
}) {
  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className="bg-card/95 backdrop-blur-md shadow-lg rounded-lg border border-border/70">
        <CollapsibleTrigger asChild>
          <Button
            variant="ghost"
            className="h-10 gap-2 sm:px-4 px-2"
          >
            <Settings className="w-4 h-4" />
            <span className="hidden sm:inline">Map Settings</span>
          </Button>
        </CollapsibleTrigger>

        <CollapsibleContent>
            <div className="p-4 space-y-3 min-w-[220px] border-t border-border/30">
              <div className="space-y-3">
                <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Display Options
                </Label>

                {/* Show Outlines */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm">Show Outlines</span>
                    {settingsLocked && <Lock className="w-3 h-3 text-muted-foreground" />}
                  </div>
                  <Switch
                    checked={!mapSettings.hideOutlines}
                    onCheckedChange={(checked: boolean) => handleToggle("hideOutlines", !checked)}
                    disabled={settingsLocked}
                  />
                </div>

                {/* Hover Highlighting */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm">Hover Highlighting</span>
                    {settingsLocked && <Lock className="w-3 h-3 text-muted-foreground" />}
                  </div>
                  <Switch
                    checked={!mapSettings.disableHover}
                    onCheckedChange={(checked: boolean) => handleToggle("disableHover", !checked)}
                    disabled={settingsLocked}
                  />
                </div>

                {/* Answer Feedback */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm">Answer Feedback</span>
                    {settingsLocked && <Lock className="w-3 h-3 text-muted-foreground" />}
                  </div>
                  <Switch
                    checked={!mapSettings.hideIndicatorColors}
                    onCheckedChange={(checked: boolean) => handleToggle("hideIndicatorColors", !checked)}
                    disabled={settingsLocked}
                  />
                </div>

                {/* Show Labels */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm">Show Labels</span>
                    {settingsLocked && <Lock className="w-3 h-3 text-muted-foreground" />}
                  </div>
                  <Switch
                    checked={!mapSettings.hideLabels}
                    onCheckedChange={(checked: boolean) => handleToggle("hideLabels", !checked)}
                    disabled={settingsLocked}
                  />
                </div>
              </div>

              <Separator />

              <div className="text-xs text-muted-foreground">
                {settingsLocked ? (
                  <span className="flex items-center gap-1">
                    <Lock className="w-3 h-3" />
                    Settings locked after first question
                  </span>
                ) : (
                  "Changes apply immediately"
                )}
              </div>
            </div>
          </CollapsibleContent>
        </div>
      </Collapsible>
  );
}
